import { usePOSSummary } from "@faire/retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useInsiderNotificationType } from "@faire/retailer-visitor-shared/hooks/accountMenu/useInsiderNotificationType";
import { useInsiderState } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderState";
import { useInsider } from "@faire/retailer-visitor-shared/hooks/retailer/useInsider";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useAssignEnableRetailerShippingPreferencePage, useSettingEnableRetailerShippingPreferencePage } from "@faire/retailer-visitor-shared/settings/useSettingEnableRetailerShippingPreferencePage";
import { useBrandDiscounts } from "@faire/retailer-visitor-shared/stores/domain/useBrandDiscounts";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { cartHasValidBrandDiscounts } from "@faire/retailer-visitor-shared/views/settings/cartHasValidBrandDiscounts";
import { SIDE_BAR_HEADINGS } from "@faire/retailer-visitor-shared/views/settings/profile/consts";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { trackRetailerStoreManagementAccountSettingsStoresLinkClick } from "@faire/web-api--source/events/retailerStoreManagement/click/accountSettingsStoresLink";
import { trackRetailerStoreManagementAccountSettingsStoresLinkView } from "@faire/web-api--source/events/retailerStoreManagement/view/accountSettingsStoresLink";
import { trackUpmarketMyTeamsLinkAccountSettingsClick } from "@faire/web-api--source/events/upmarket/click/myTeamsLinkAccountSettings";
import { trackUpmarketMyTeamsLinkAccountSettingsView } from "@faire/web-api--source/events/upmarket/view/myTeamsLinkAccountSettings";
import availableCredit from "@faire/web-api--source/routes/www/retailer/settings/available-credit";
import emailPreferences from "@faire/web-api--source/routes/www/retailer/settings/email-preferences";
import insiderMembership from "@faire/web-api--source/routes/www/retailer/settings/insider-membership";
import myTeam from "@faire/web-api--source/routes/www/retailer/settings/my-team";
import paymentMethods from "@faire/web-api--source/routes/www/retailer/settings/payment-methods";
import posManager from "@faire/web-api--source/routes/www/retailer/settings/pos-manager";
import profileSettings from "@faire/web-api--source/routes/www/retailer/settings/profile";
import securitySettings from "@faire/web-api--source/routes/www/retailer/settings/security";
import retailerSettingsShippingPreferences from "@faire/web-api--source/routes/www/retailer/settings/shipping-preferences";
import stores from "@faire/web-api--source/routes/www/retailer/settings/stores";
import { useMemo, useEffect } from "react";
export var NotificationIconType;
(function(NotificationIconType) {
    NotificationIconType["NOTIFICATION"] = "notification";
    NotificationIconType["CRITICAL"] = "critical";
})(NotificationIconType || (NotificationIconType = {}));
/** Provides the list of menu items for the settings sidebar. */ export var useMenuItems = function() {
    var _usePOSSummary = usePOSSummary(), hasLinkedPOS = _usePOSSummary.hasLinkedPOS, name = _usePOSSummary.name;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var accountType = useUserStore([
        "isRetailerSubUser",
        "accountType"
    ]).accountType;
    var faireCredit = useRetailer().faireCredit;
    var isEligibleForInsider = useInsider().isEligibleForInsider;
    var _useInsiderState = useInsiderState(), isInsiderPaused = _useInsiderState.isInsiderPaused, isInsiderActive = _useInsiderState.isInsiderActive;
    var insiderNotificationType = useInsiderNotificationType();
    var brandDiscounts = useBrandDiscounts().brandDiscounts;
    var enableShippingPreferencePage = useSettingEnableRetailerShippingPreferencePage();
    var assignEnableRetailerShippingPreferencePage = useAssignEnableRetailerShippingPreferencePage();
    useEffect(function() {
        assignEnableRetailerShippingPreferencePage();
    }, [
        assignEnableRetailerShippingPreferencePage
    ]);
    return useMemo(function() {
        var hasCredits = faireCredit > 0 || cartHasValidBrandDiscounts(brandDiscounts);
        var menuItems = [
            {
                value: "profile",
                label: strictLocalize({
                    id: "LjM//z",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Profile"
                        }
                    ]
                }),
                path: profileSettings(),
                section: SIDE_BAR_HEADINGS.SETTINGS
            },
            {
                value: "stores-management",
                label: strictLocalize({
                    id: "8D3MhI",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Stores"
                        }
                    ]
                }),
                handleClick: function() {
                    return trackRetailerStoreManagementAccountSettingsStoresLinkClick();
                },
                path: stores(),
                handleOnVisible: function() {
                    return trackRetailerStoreManagementAccountSettingsStoresLinkView();
                },
                section: SIDE_BAR_HEADINGS.DEFAULT
            },
            {
                value: "my-team",
                label: strictLocalize({
                    id: "mp1lSN",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Team"
                        }
                    ]
                }),
                path: myTeam(),
                handleClick: function() {
                    return trackUpmarketMyTeamsLinkAccountSettingsClick(accountType);
                },
                handleOnVisible: function() {
                    return trackUpmarketMyTeamsLinkAccountSettingsView(accountType);
                },
                section: SIDE_BAR_HEADINGS.DEFAULT
            }
        ];
        if (hasCredits) {
            menuItems.push({
                value: "available-credit",
                label: strictLocalize({
                    id: "6/Xy8O",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Available credit"
                        }
                    ]
                }),
                path: availableCredit(),
                section: SIDE_BAR_HEADINGS.DEFAULT
            });
        }
        menuItems.push({
            value: "payment_methods",
            label: strictLocalize({
                id: "KoL5bc",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Payment methods"
                    }
                ]
            }),
            path: paymentMethods(),
            section: SIDE_BAR_HEADINGS.DEFAULT
        });
        if (enableShippingPreferencePage) {
            menuItems.push({
                value: "shipping_preferences",
                label: strictLocalize({
                    id: "0VHTck",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Shipping"
                        }
                    ]
                }),
                path: retailerSettingsShippingPreferences()
            });
        }
        if (hasLinkedPOS) {
            menuItems.push({
                value: "pos-manager",
                label: strictLocalize({
                    id: "wGHX5j",
                    defaultMessage: [
                        {
                            type: 1,
                            value: "posCompanyName"
                        },
                        {
                            type: 0,
                            value: " integration"
                        }
                    ]
                }, {
                    posCompanyName: name
                }),
                path: posManager(),
                section: SIDE_BAR_HEADINGS.DEFAULT
            });
        }
        var insiderLabel = strictLocalize({
            id: "Eh9Kki",
            defaultMessage: [
                {
                    type: 0,
                    value: "Insider"
                }
            ]
        });
        if (isInsiderActive || isInsiderPaused || isEligibleForInsider) {
            menuItems.push({
                value: "insider_membership",
                label: insiderLabel,
                path: insiderMembership(),
                notificationIconType: isInsiderActive || isInsiderPaused ? insiderNotificationType : undefined,
                section: SIDE_BAR_HEADINGS.DEFAULT
            });
        }
        menuItems.push({
            value: "email_preferences",
            label: strictLocalize({
                id: "AtDx5j",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Email preferences"
                    }
                ]
            }),
            path: emailPreferences(),
            section: SIDE_BAR_HEADINGS.SETTINGS
        });
        menuItems.push({
            value: "security",
            label: strictLocalize({
                id: "1Jt3RS",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Security & privacy"
                    }
                ]
            }),
            path: securitySettings(),
            section: SIDE_BAR_HEADINGS.SETTINGS
        });
        return menuItems;
    }, [
        faireCredit,
        brandDiscounts,
        strictLocalize,
        hasLinkedPOS,
        isInsiderActive,
        isInsiderPaused,
        isEligibleForInsider,
        name,
        insiderNotificationType,
        accountType,
        enableShippingPreferencePage
    ]);
};
